import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { StudioMessageService } from '../services/studio-message.service';

/**
 * Extracts a user-friendly error message from an HttpErrorResponse.
 * Tries multiple fallback strategies to find the most relevant message.
 *
 * Priority order:
 * 1. error.error.message (backend error message)
 * 2. error.error.error (alternative backend error format)
 * 3. error.error (if it's a string)
 * 4. error.statusText (HTTP status text)
 * 5. Generic fallback message
 *
 * @param error - The HttpErrorResponse to extract message from
 * @returns User-friendly error message string
 */
export function extractErrorMessage(error: any): string {
  if (error instanceof HttpErrorResponse) {
    // Try to get backend error message (most specific)
    if (error.error?.message && typeof error.error.message === 'string') {
      return error.error.message;
    }

    // Alternative backend error format
    if (error.error?.error && typeof error.error.error === 'string') {
      return error.error.error;
    }

    // Sometimes error.error is directly a string
    if (typeof error.error === 'string' && error.error.trim()) {
      return error.error;
    }

    // Fall back to HTTP status text
    if (error.statusText && error.statusText !== 'Unknown Error') {
      return error.statusText;
    }
  }

  // Final fallback - check if error has message property
  if (error?.message && typeof error.message === 'string') {
    // Avoid showing technical HTTP failure messages to users
    if (!error.message.startsWith('Http failure')) {
      return error.message;
    }
  }

  return 'An unexpected error occurred';
}

/**
 * Maps HTTP status codes to translation keys for user-friendly error messages.
 *
 * @param status - HTTP status code
 * @returns Translation key for the error message
 */
export function getHttpErrorTranslationKey(status: number): string {
  switch (status) {
    case 400:
      return 'studio.errors.http.400';
    case 401:
      return 'studio.errors.http.401';
    case 403:
      return 'studio.errors.http.403';
    case 404:
      return 'studio.errors.http.404';
    case 500:
      return 'studio.errors.http.500';
    case 503:
      return 'studio.errors.http.503';
    case 0: // Network error (no response from server)
      return 'studio.errors.http.network';
    default:
      return 'studio.errors.http.unknown';
  }
}

/**
 * Determines the message type (severity) based on HTTP status code.
 *
 * @param status - HTTP status code
 * @returns Message type: 'warning' for 4xx errors, 'alert' for others
 */
export function getErrorSeverity(status: number): 'warning' | 'alert' {
  // 4xx errors are typically user errors (warnings)
  if (status >= 400 && status < 500) {
    return 'warning';
  }
  // 5xx errors and network errors are critical (alerts)
  return 'alert';
}

/**
 * Handles Studio module errors in a unified way:
 * - Extracts user-friendly messages
 * - Translates HTTP errors
 * - Displays notifications via StudioMessageService
 * - Provides operation context
 *
 * @param error - The error to handle (typically HttpErrorResponse)
 * @param translateService - Angular TranslateService for i18n
 * @param messageService - StudioMessageService for displaying notifications
 * @param operationKey - Translation key for the operation context (e.g., 'studio.errors.operations.saveFailed')
 * @returns The extracted error message for use in component state
 *
 * @example
 * ```typescript
 * catchError((error) => {
 *   const errorMessage = handleStudioError(
 *     error,
 *     this.translateService,
 *     this.messageService,
 *     'studio.errors.operations.saveFailed'
 *   );
 *   return of({ error: errorMessage, loading: false });
 * })
 * ```
 */
export function handleStudioError(
  error: any,
  translateService: TranslateService,
  messageService: StudioMessageService,
  operationKey: string
): string {
  // Extract the most relevant error message
  const backendMessage = extractErrorMessage(error);

  // Get HTTP status code (if available)
  const status = error instanceof HttpErrorResponse ? error.status : 0;

  // Get translated HTTP error message
  const httpErrorKey = getHttpErrorTranslationKey(status);
  const httpErrorMessage = translateService.instant(httpErrorKey);

  // Get operation context (e.g., "Failed to save changes")
  const operationMessage = translateService.instant(operationKey);

  // Determine message severity
  const severity = getErrorSeverity(status);

  // Build the complete error message
  // Title: Operation context + HTTP error type
  // Details: Backend-specific message (if different from HTTP error)
  const title = operationMessage;
  const details =
    backendMessage !== httpErrorMessage && backendMessage !== 'An unexpected error occurred'
      ? `${httpErrorMessage}: ${backendMessage}`
      : httpErrorMessage;

  // Display notification to user
  if (severity === 'warning') {
    messageService.warning(title, details);
  } else {
    messageService.error(title, details);
  }

  // Return the message for component state (if needed)
  return details;
}
